import { useState } from 'react';
import Button from '@/components/base/Button';
import Modal from '@/components/base/Modal';
import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney } from '@/utils/format';
import type { Payout } from '@/types/portal';

interface RetryPayoutModalProps {
  payout: Payout | null;
  onClose: () => void;
  onConfirm: () => void;
  retried: boolean;
}

export default function RetryPayoutModal({
  payout,
  onClose,
  onConfirm,
  retried,
}: RetryPayoutModalProps) {
  const [note, setNote] = useState('');

  if (!payout) {
    return null;
  }

  const close = () => {
    setNote('');
    onClose();
  };

  if (retried) {
    return (
      <Modal
        open
        onClose={close}
        icon="ri-refresh-line"
        title="Retry queued"
        subtitle={`${payout.reference} will be re-attempted on ${payout.rail}.`}
        footer={
          <Button icon="ri-check-line" onClick={close}>
            Done
          </Button>
        }
      >
        <div className="flex flex-col items-center py-2 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-2xl text-accent-700">
            <i className="ri-check-line" />
          </span>
          <p className="mt-3 font-heading text-2xl font-semibold text-foreground-950">
            {formatMoney(payout.amount, payout.currency)}
          </p>
          <div className="mt-2">
            <StatusBadge status="Processing" />
          </div>
          <p className="mt-4 text-xs text-foreground-500">
            The payout re-enters the rail queue and will settle on the next successful attempt. Track
            it in Transactions.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={close}
      icon="ri-refresh-line"
      title={`Retry ${payout.reference}`}
      subtitle="Re-attempt this disbursement on the selected rail."
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button
            icon="ri-refresh-line"
            onClick={() => {
              onConfirm();
              setNote('');
            }}
          >
            Confirm retry
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-md border border-background-200 bg-background-100 px-3 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground-900">{payout.creatorName}</span>
            <span className="text-sm font-medium text-foreground-950">
              {formatMoney(payout.amount, payout.currency)}
            </span>
          </div>
          <p className="mt-1 text-xs text-foreground-500">
            {payout.campaign} · {payout.rail}
          </p>
        </div>

        {payout.exception ? (
          <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            <span>{payout.exception}</span>
          </div>
        ) : null}

        <div>
          <label
            htmlFor="retry-note"
            className="text-xs font-label font-medium text-foreground-600"
          >
            Retry note (optional)
          </label>
          <textarea
            id="retry-note"
            value={note}
            maxLength={500}
            rows={3}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add context for the audit trail..."
            className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <p className="mt-1 text-right text-[11px] text-foreground-400">{note.length}/500</p>
        </div>

        <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2.5 text-sm text-secondary-900">
          <i className="ri-information-line mt-0.5" />
          <span>
            A retry debits the funding wallet only when settlement succeeds. Failed attempts are
            returned automatically.
          </span>
        </div>
      </div>
    </Modal>
  );
}